import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { db } from '../../../../utils/init-firebase';
import { useAuth } from '../../../../contexts/AuthContext';
import { ActiveMeeting, COLLECTION_NAMES, User } from '../../types';

export interface MeetingState {
  activeMeeting: ActiveMeeting | null;
  sectionElapsedSeconds: number;
  totalElapsedSeconds: number;
  isPresenter: boolean;
  canPresent: boolean;
  loading: boolean;
  error: Error | null;
  startMeeting: (date: string) => Promise<void>;
  stopMeeting: () => Promise<void>;
  updateSection: (sectionIndex: number) => Promise<void>;
  updateFilters: (userId: string, idsTab: string) => Promise<void>;
  updateRockTypeFilter: (rockType: 'all' | 'company' | 'individual') => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
}

export function useActiveMeeting(): MeetingState {
  const { currentUser } = useAuth();
  const [activeMeeting, setActiveMeeting] = useState<ActiveMeeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [canPresent, setCanPresent] = useState(false);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isPresenter = activeMeeting?.presenterUserId === currentUser?.uid;

  const sectionElapsedSeconds = activeMeeting
    ? Math.floor((currentTime - (activeMeeting.sectionStartedAt instanceof Timestamp 
      ? activeMeeting.sectionStartedAt.toMillis() 
      : new Date(activeMeeting.sectionStartedAt).getTime())) / 1000)
    : 0;

  const totalElapsedSeconds = activeMeeting
    ? Math.floor((currentTime - (activeMeeting.totalStartedAt instanceof Timestamp 
      ? activeMeeting.totalStartedAt.toMillis() 
      : new Date(activeMeeting.totalStartedAt).getTime())) / 1000)
    : 0;

  const fetchActiveMeeting = useCallback(async () => {
    try {
      const q = query(
        collection(db, COLLECTION_NAMES.ACTIVE_MEETINGS),
        where('isActive', '==', true),
        limit(1)
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        setActiveMeeting(null);
      } else {
        const doc = snapshot.docs[0];
        const data = doc.data();
        setActiveMeeting({
          id: doc.id,
          ...data,
          meetingTimestamp: data.meetingTimestamp,
          meetingDate: data.meetingDate,
          sectionStartedAt: data.sectionStartedAt,
          totalStartedAt: data.totalStartedAt,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        } as ActiveMeeting);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching active meeting:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveMeeting();
  }, [fetchActiveMeeting]);

  useEffect(() => {
    const fetchUserCanPresent = async () => {
      if (!currentUser?.uid) {
        setCanPresent(false);
        return;
      }

      try {
        const userDocRef = doc(db, COLLECTION_NAMES.USERS, currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data() as User;
          setCanPresent(userData.canPresentMeetings || false);
        } else {
          setCanPresent(false);
        }
      } catch (err) {
        console.error('Error fetching user canPresentMeetings:', err);
        setCanPresent(false);
      }
    };

    fetchUserCanPresent();
  }, [currentUser?.uid]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchActiveMeeting();
      }
    };

    const handleFocus = () => {
      fetchActiveMeeting();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchActiveMeeting]);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;

    pollingIntervalRef.current = setInterval(() => {
      if (!isPresenter) {
        fetchActiveMeeting();
      }
    }, 3000);
  }, [isPresenter, fetchActiveMeeting]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (activeMeeting && !isPresenter) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [activeMeeting, isPresenter, startPolling, stopPolling]);

  useEffect(() => {
    if (activeMeeting) {
      timerIntervalRef.current = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [activeMeeting]);

  const startMeeting = useCallback(async (date: string) => {
    if (!canPresent) {
      throw new Error('User does not have permission to present meetings');
    }

    try {
      const now = Timestamp.now();

      // Check for existing active meeting
      const existingQuery = query(
        collection(db, COLLECTION_NAMES.ACTIVE_MEETINGS),
        where('isActive', '==', true),
        limit(1)
      );
      const existingSnapshot = await getDocs(existingQuery);

      if (!existingSnapshot.empty) {
        const existing = existingSnapshot.docs[0].data();
        if (existing.presenterUserId === currentUser?.uid) {
          await fetchActiveMeeting();
          return;
        } else {
          throw new Error('A meeting is already in progress');
        }
      }

      // Create new active meeting
      const newMeeting = {
        meetingTimestamp: now,
        meetingDate: date,
        currentSectionIndex: 0,
        sectionStartedAt: now,
        totalStartedAt: now,
        presenterUserId: currentUser?.uid || '',
        selectedUserId: 'all',
        rockTypeFilter: 'all',
        idsFilterTab: 'all',
        isActive: true,
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAMES.ACTIVE_MEETINGS), newMeeting);
      
      setActiveMeeting({
        id: docRef.id,
        ...newMeeting,
      } as ActiveMeeting);
    } catch (err) {
      console.error('Error starting meeting:', err);
      throw err;
    }
  }, [canPresent, currentUser?.uid, fetchActiveMeeting]);

  const stopMeeting = useCallback(async () => {
    if (!activeMeeting || !isPresenter) {
      throw new Error('Not authorized to stop meeting');
    }

    try {
      await deleteDoc(doc(db, COLLECTION_NAMES.ACTIVE_MEETINGS, activeMeeting.id));
      setActiveMeeting(null);
    } catch (err) {
      console.error('Error stopping meeting:', err);
      throw err;
    }
  }, [activeMeeting, isPresenter]);

  const updateSection = useCallback(async (sectionIndex: number) => {
    if (!activeMeeting || !isPresenter) {
      throw new Error('Not authorized to update section');
    }

    try {
      const now = Timestamp.now();
      const meetingRef = doc(db, COLLECTION_NAMES.ACTIVE_MEETINGS, activeMeeting.id);

      await updateDoc(meetingRef, {
        currentSectionIndex: sectionIndex,
        sectionStartedAt: now,
        updatedAt: now,
      });

      setActiveMeeting({
        ...activeMeeting,
        currentSectionIndex: sectionIndex,
        sectionStartedAt: now,
        updatedAt: now,
      });
    } catch (err) {
      console.error('Error updating section:', err);
      throw err;
    }
  }, [activeMeeting, isPresenter]);

  const updateFilters = useCallback(async (userId: string, idsTab: string) => {
    if (!activeMeeting || !isPresenter) {
      throw new Error('Not authorized to update filters');
    }

    try {
      const meetingRef = doc(db, COLLECTION_NAMES.ACTIVE_MEETINGS, activeMeeting.id);

      await updateDoc(meetingRef, {
        selectedUserId: userId,
        idsFilterTab: idsTab,
        updatedAt: Timestamp.now(),
      });

      setActiveMeeting({
        ...activeMeeting,
        selectedUserId: userId,
        idsFilterTab: idsTab,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error updating filters:', err);
      throw err;
    }
  }, [activeMeeting, isPresenter]);

  const updateRockTypeFilter = useCallback(async (rockType: 'all' | 'company' | 'individual') => {
    if (!activeMeeting || !isPresenter) {
      throw new Error('Not authorized to update rock type filter');
    }

    try {
      const meetingRef = doc(db, COLLECTION_NAMES.ACTIVE_MEETINGS, activeMeeting.id);

      await updateDoc(meetingRef, {
        rockTypeFilter: rockType,
        updatedAt: Timestamp.now(),
      });

      setActiveMeeting({
        ...activeMeeting,
        rockTypeFilter: rockType,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error updating rock type filter:', err);
      throw err;
    }
  }, [activeMeeting, isPresenter]);

  return {
    activeMeeting,
    sectionElapsedSeconds,
    totalElapsedSeconds,
    isPresenter,
    canPresent,
    loading,
    error,
    startMeeting,
    stopMeeting,
    updateSection,
    updateFilters,
    updateRockTypeFilter,
    startPolling,
    stopPolling,
  };
}

